import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, ChevronRight, Sparkles, User, Trophy, TrendingUp, Award } from 'lucide-react';
import api from '../utils/api';
import { Button } from './ui/button';
import AttachLoyaltyDialog from './revenues/AttachLoyaltyDialog';

/**
 * Reminds the barber (or admin) about recent revenues (<3 days) that haven't
 * been linked to a loyalty card. One-click attach via dialog.
 * - Admin: detailed list with date + barber name + per-barber summary.
 * - Barber: own unlinked only — if everything is linked, a daily praise card is shown.
 */
const UnlinkedRevenuesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attachingRevenue, setAttachingRevenue] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/loyalty/unlinked-recent-revenues');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (loading || !data) return null;

  const isAdmin = data.role === 'admin';
  const isBarberAllLinked = data.role === 'barber' && data.all_linked;
  const perf = data.performance_30d?.barbers || [];
  const myPerf = data.my_performance || null;

  // Build the "performance comparison" block — reusable both inside and outside
  // the overdue panel so the section appears even when there's nothing overdue.
  const renderPerformance = () => {
    if (perf.length === 0) return null;
    const topPct = perf[0]?.linked_pct || 0;
    return (
      <div className="bg-card border border-emerald-500/30 rounded-xl p-4 shadow-lg" data-testid="loyalty-perf-comparison">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-amber-400" />
          <h3 className="font-bold">أداء الحلاقين في برنامج الولاء — آخر 30 يوم</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          نسبة الإيرادات المربوطة ببطاقة ولاء لكل حلاق + ترتيبهم — كلما زادت النسبة، زادت قدرة الحلاق على بناء عملاء أوفياء.
        </p>

        {/* My summary (barber view) */}
        {!isAdmin && myPerf && (
          <div className="mb-3 p-3 bg-emerald-500/5 border border-emerald-500/30 rounded-lg" data-testid="my-perf-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-bold text-emerald-300 flex items-center gap-1">
                <Award className="w-4 h-4" /> أداؤك
              </span>
              <span className="text-xs text-muted-foreground">
                المركز {myPerf.rank} من {perf.length}
                {myPerf.is_top && ' 🏆'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-3 bg-secondary/60 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600"
                  style={{ width: `${Math.min(myPerf.linked_pct, 100)}%` }}
                />
              </div>
              <span className="text-sm font-bold text-emerald-400 min-w-[50px] text-end">
                {myPerf.linked_pct}%
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-3 flex-wrap">
              <span>إجمالي: {myPerf.total_revenues}</span>
              <span className="text-emerald-400">مربوط: {myPerf.linked_count}</span>
              <span className="text-amber-400">غير مربوط: {myPerf.unlinked_count}</span>
              {!myPerf.is_top && topPct > myPerf.linked_pct && (
                <span className="text-blue-300 inline-flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  المتفوّق عليك: {perf[0].barber_name} ({topPct}%) — فارق {(topPct - myPerf.linked_pct).toFixed(1)} نقطة
                </span>
              )}
            </div>
          </div>
        )}

        {/* Leaderboard (admin sees all; barber sees the full ranking too) */}
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {perf.map((row) => {
            const isMe = !isAdmin && myPerf && row.barber_id === myPerf.barber_id;
            return (
              <div
                key={row.barber_id}
                className={`flex items-center gap-3 p-2 rounded-lg border ${isMe ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-secondary/30 border-white/5'}`}
                data-testid={`perf-row-${row.barber_id}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  row.rank === 1 ? 'bg-amber-500/20 text-amber-400'
                    : row.rank === 2 ? 'bg-slate-400/20 text-slate-300'
                    : row.rank === 3 ? 'bg-orange-600/20 text-orange-400'
                    : 'bg-secondary/60 text-muted-foreground'
                }`}>
                  {row.rank === 1 ? '🥇' : row.rank === 2 ? '🥈' : row.rank === 3 ? '🥉' : row.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate">
                      {row.barber_name}
                      {isMe && <span className="ms-1 text-xs text-emerald-400">(أنت)</span>}
                    </span>
                    <span className={`text-sm font-bold ${row.linked_pct >= 75 ? 'text-emerald-400' : row.linked_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {row.linked_pct}%
                    </span>
                  </div>
                  <div className="h-2 bg-secondary/60 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${row.linked_pct >= 75 ? 'bg-emerald-500' : row.linked_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(row.linked_pct, 100)}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {row.linked_count} مربوط / {row.total_revenues} إجمالي ({row.unlinked_count} بدون ربط)
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // BARBER + ALL LINKED → praise card + performance comparison below
  if (isBarberAllLinked) {
    return (
      <div className="space-y-4">
        <div
          className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/40 rounded-xl p-5 shadow-lg relative overflow-hidden"
          data-testid="all-linked-praise-card"
        >
          <div className="absolute -top-4 -end-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-6 -start-6 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="relative flex items-start gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-emerald-300 mb-1">عمل رائع 👏</h3>
              <p className="text-sm text-emerald-100/90 leading-relaxed">
                {data.praise}
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-emerald-200/70">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                  ✅ {data.linked_count} إيراد مربوط خلال الأسبوع الماضي
                </span>
              </div>
            </div>
          </div>
        </div>
        {renderPerformance()}
      </div>
    );
  }

  // No items + barber didn't have any → render only the performance comparison
  if (data.count === 0 && !isAdmin) {
    return renderPerformance();
  }

  // Admin with no unlinked → show only the performance comparison
  if (data.count === 0 && isAdmin) {
    return renderPerformance();
  }

  return (
    <div className="space-y-4">
      <div
        className="bg-card border border-amber-500/30 rounded-xl p-4 shadow-lg"
        data-testid="unlinked-revenues-widget"
      >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-500" />
          <h3 className="font-bold">
            {isAdmin
              ? 'إيرادات متأخرة (≥7 أيام) بدون ربط بطاقة ولاء'
              : 'إيراداتك المتأخرة (≥7 أيام) بدون ربط بطاقة ولاء'}
          </h3>
          <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-bold animate-pulse">
            {data.count}
          </span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="text-primary"
          onClick={() => navigate('/my-revenues')}
        >
          عرض كل الإيرادات <ChevronRight className="w-4 h-4 ms-1" />
        </Button>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        {isAdmin
          ? 'هذه الإيرادات مرّ عليها أكثر من 7 أيام دون ربطها بأي بطاقة ولاء — تابع مع الحلاقين.'
          : 'هذه الإيرادات مرّ عليها أكثر من 7 أيام دون ربط — يلزم إذن مدير للربط الآن.'}
      </p>

      {/* Admin: per-barber summary at the top */}
      {isAdmin && data.by_barber && data.by_barber.length > 0 && (
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="unlinked-by-barber">
          {data.by_barber.slice(0, 6).map((row) => (
            <div
              key={row.barber_id}
              className="flex items-center justify-between bg-secondary/40 rounded-lg px-3 py-2 border border-white/5"
            >
              <div className="flex items-center gap-2 min-w-0">
                <User className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <span className="text-sm font-medium truncate">{row.barber_name}</span>
              </div>
              <div className="text-end flex-shrink-0">
                <div className="text-xs font-bold text-amber-400">
                  {row.unlinked_count} إيراد
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {Number(row.unlinked_amount).toFixed(0)} د.إ
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detailed list */}
      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {data.items.slice(0, 10).map((rev) => {
          const date = (rev.created_at || rev.date || '').substring(0, 10);
          return (
            <div
              key={rev.id}
              className="flex items-center justify-between gap-2 bg-secondary/30 rounded px-3 py-2 text-sm hover:bg-secondary/50 transition-colors"
              data-testid={`unlinked-row-${rev.id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                <span className="font-bold text-primary whitespace-nowrap">
                  {Number(rev.amount).toFixed(2)} د.إ
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  · {date}
                </span>
                {isAdmin && (
                  <span className="text-xs text-amber-400 inline-flex items-center gap-1 whitespace-nowrap">
                    <User className="w-3 h-3" />
                    {rev.barber_name || '—'}
                  </span>
                )}
                {rev.description && (
                  <span className="text-xs text-muted-foreground/80 truncate">
                    · {rev.description}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-amber-500 hover:bg-amber-500/10 flex-shrink-0"
                onClick={() => setAttachingRevenue(rev)}
                data-testid={`widget-attach-${rev.id}`}
              >
                <Crown className="w-3 h-3 me-1" />
                ربط الآن
              </Button>
            </div>
          );
        })}
        {data.items.length > 10 && (
          <p className="text-[11px] text-center text-muted-foreground pt-1">
            و {data.items.length - 10} إيراد آخر…
          </p>
        )}
      </div>

      <AttachLoyaltyDialog
        open={!!attachingRevenue}
        onOpenChange={(v) => !v && setAttachingRevenue(null)}
        revenue={attachingRevenue}
        onSuccess={() => {
          setAttachingRevenue(null);
          fetchData();
        }}
      />
      </div>
      {renderPerformance()}
    </div>
  );
};

export default UnlinkedRevenuesWidget;
